import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { ApiClientError, adoptToken, isRecord, requestJson, signInBody } from "./http.js";
import { DEFAULT_POLICIES, POLICY_MIN_PASSWORD_FLOOR, isPolicies } from "./policies.js";
import type { Policies } from "./policies.js";
import { isAdminRecoveryStatus } from "./recovery.js";
import type { AdminRecoveryStatus } from "./recovery.js";
import { bearerToken } from "./token.js";

export interface IdEmail {
  readonly id: string;
  readonly email: string;
}

export interface SaltPayload {
  readonly email: string;
  readonly saltB64: string;
}

export interface LoginResult extends IdEmail {
  readonly twoFactorRequired: boolean;
  /** Admin-created account on its one-time password: the ticket only allows setting a new one. */
  readonly passwordChangeRequired?: boolean | undefined;
  readonly ticket?: string | undefined;
  /** One-time password accepted, but the account has a second factor: it must pass before the password change. */
  readonly passwordChangeAfterTwoFactor?: boolean | undefined;
  /** With passwordChangeRequired after an admin recovery: the account keys sealed under the one-time password. */
  readonly keyEncB64?: string | undefined;
  /** Bearer mode only (desktop). */
  readonly token?: string | undefined;
}

export interface AuthConfig {
  /** Fresh instance without accounts: the first admin still has to be created. */
  readonly setupRequired: boolean;
  /** Policy minimum for new master passwords. */
  readonly minPasswordLength: number;
}

export type AccountRole = "admin" | "user";

export interface Me extends IdEmail {
  readonly role: AccountRole;
  readonly twoFactorEnabled: boolean;
  readonly passwordChangedAt: number | null;
  /** The 2FA policy applies and the account has no second factor yet: vault and teams stay closed. */
  readonly twoFactorSetupRequired: boolean;
  readonly policies: Policies;
  /** Null while the instance has no recovery key. */
  readonly adminRecovery: AdminRecoveryStatus | null;
}

function isIdEmail(value: unknown): value is IdEmail {
  return isRecord(value) && typeof value["id"] === "string" && typeof value["email"] === "string";
}

export function isLoginResult(value: unknown): value is LoginResult {
  if (!isIdEmail(value)) {
    return false;
  }
  if (!("twoFactorRequired" in value) || typeof value.twoFactorRequired !== "boolean") {
    return false;
  }
  if ("token" in value && value.token !== undefined && typeof value.token !== "string") {
    return false;
  }
  if ("passwordChangeRequired" in value && value.passwordChangeRequired !== undefined && typeof value.passwordChangeRequired !== "boolean") {
    return false;
  }
  if ("passwordChangeAfterTwoFactor" in value && value.passwordChangeAfterTwoFactor !== undefined && typeof value.passwordChangeAfterTwoFactor !== "boolean") {
    return false;
  }
  if ("keyEncB64" in value && value.keyEncB64 !== undefined && typeof value.keyEncB64 !== "string") {
    return false;
  }
  if (!("ticket" in value) || value.ticket === undefined) {
    return true;
  }
  return typeof value.ticket === "string";
}

/** Server answer to /auth/config. A server without policies sends no length; the built-in floor applies. */
export function parseAuthConfig(value: unknown): AuthConfig | undefined {
  if (!isRecord(value) || typeof value["setupRequired"] !== "boolean") {
    return undefined;
  }
  const length = value["minPasswordLength"];
  return {
    setupRequired: value["setupRequired"],
    minPasswordLength: typeof length === "number" && length > POLICY_MIN_PASSWORD_FLOOR ? length : POLICY_MIN_PASSWORD_FLOOR
  };
}

/** Server answer to /auth/me, with the policy fields defaulted for servers that predate them. */
export function parseMe(value: unknown): Me | undefined {
  if (
    !isIdEmail(value) ||
    !isRecord(value) ||
    (value["role"] !== "admin" && value["role"] !== "user") ||
    typeof value["twoFactorEnabled"] !== "boolean" ||
    (typeof value["passwordChangedAt"] !== "number" && value["passwordChangedAt"] !== null)
  ) {
    return undefined;
  }
  return {
    id: value.id,
    email: value.email,
    role: value["role"],
    twoFactorEnabled: value["twoFactorEnabled"],
    passwordChangedAt: value["passwordChangedAt"],
    twoFactorSetupRequired: value["twoFactorSetupRequired"] === true,
    policies: isPolicies(value["policies"]) ? value["policies"] : DEFAULT_POLICIES,
    adminRecovery: isAdminRecoveryStatus(value["adminRecovery"]) ? value["adminRecovery"] : null
  };
}

function isSaltPayload(value: unknown): value is SaltPayload {
  return isRecord(value) && typeof value["email"] === "string" && typeof value["saltB64"] === "string";
}

function isRequestOptions(value: unknown): value is PublicKeyCredentialRequestOptionsJSON {
  return isRecord(value) && typeof value["challenge"] === "string";
}

function unexpected(): ApiClientError {
  return new ApiClientError(500, "Unerwartete Antwort vom Server.");
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const config = parseAuthConfig(await requestJson("/api/v1/auth/config"));
  if (config === undefined) {
    throw unexpected();
  }
  return config;
}

export async function fetchSalt(email: string): Promise<SaltPayload> {
  const body = await requestJson("/api/v1/auth/salt", { method: "POST", body: { email } });
  if (!isSaltPayload(body)) {
    throw unexpected();
  }
  return body;
}

/** Creates the first admin of a fresh instance. The code comes from the server log. */
export async function setupInstance(code: string, email: string, saltB64: string, authKeyB64: string, keyEncB64: string): Promise<IdEmail> {
  const body = await requestJson("/api/v1/auth/setup", {
    method: "POST",
    body: { code, email, saltB64, authKeyB64, keyEncB64, ...signInBody() }
  });
  if (!isIdEmail(body)) {
    throw unexpected();
  }
  adoptToken(body);
  return body;
}

/** Replaces the one-time password with the user's own master password, using the ticket from login. */
/** keptKeys: the existing account keys were re-sealed (after an admin recovery) rather than created fresh. */
export async function setInitialPassword(ticket: string, saltB64: string, authKeyB64: string, keyEncB64: string, keptKeys: boolean = false): Promise<IdEmail> {
  const body = await requestJson("/api/v1/auth/initial-password", {
    method: "POST",
    body: { ticket, saltB64, authKeyB64, keyEncB64, ...(keptKeys ? { accountKeys: "kept" } : {}), ...signInBody() }
  });
  if (!isIdEmail(body)) {
    throw unexpected();
  }
  adoptToken(body);
  return body;
}

export async function fetchMe(): Promise<Me> {
  const me = parseMe(await requestJson("/api/v1/auth/me"));
  if (me === undefined) {
    throw unexpected();
  }
  return me;
}

export async function login(email: string, authKeyB64: string): Promise<LoginResult> {
  const body = await requestJson("/api/v1/auth/login", {
    method: "POST",
    body: { email, authKeyB64, ...signInBody() }
  });
  if (!isLoginResult(body)) {
    throw unexpected();
  }
  adoptToken(body);
  return body;
}

export async function logout(): Promise<void> {
  try {
    await requestJson("/api/v1/auth/logout", { method: "POST", body: {} });
  } finally {
    bearerToken.clear();
  }
}

/** Extends the idle window of the server session. Returns the new expiry in epoch milliseconds. */
export async function refreshSession(): Promise<number> {
  const body = await requestJson("/api/v1/auth/refresh", { method: "POST", body: {} });
  if (!isRecord(body) || typeof body["expiresAt"] !== "number") {
    throw unexpected();
  }
  adoptToken(body);
  return body["expiresAt"];
}

/** Recovery key (and second factor) checked: the sealed account keys and the ticket for the new password. */
export interface RecoveryGrant {
  readonly ticket: string;
  readonly recoveryKeyEncB64: string;
}

/** Second factor passed on a one-time password: the ticket for the new master password, and after an admin recovery the keys. */
export interface PasswordChangeGrant {
  readonly ticket: string;
  readonly keyEncB64?: string | undefined;
}

/**
 * A sign-in ends in a session; a recovery only in the grant to set a new master password; a sign-in
 * on a one-time password in the grant to replace it.
 */
export type SecondFactorOutcome =
  | { readonly kind: "session" }
  | { readonly kind: "recovery"; readonly grant: RecoveryGrant }
  | { readonly kind: "password-change"; readonly grant: PasswordChangeGrant };

export type RecoveryStart =
  | { readonly kind: "second-factor"; readonly ticket: string }
  | { readonly kind: "granted"; readonly grant: RecoveryGrant };

function recoveryGrantOf(body: Record<string, unknown>): RecoveryGrant {
  const ticket = body["ticket"];
  const recoveryKeyEncB64 = body["recoveryKeyEncB64"];
  if (typeof ticket !== "string" || typeof recoveryKeyEncB64 !== "string") {
    throw unexpected();
  }
  return { ticket, recoveryKeyEncB64 };
}

async function completeSecondFactor(ticket: string, proof: Record<string, unknown>): Promise<SecondFactorOutcome> {
  const body = await requestJson("/api/v1/2fa/login", { method: "POST", body: { ticket, ...proof, ...signInBody() } });
  if (isRecord(body) && body["recovery"] === true) {
    return { kind: "recovery", grant: recoveryGrantOf(body) };
  }
  if (isRecord(body) && body["passwordChangeRequired"] === true) {
    const ticket = body["ticket"];
    const keyEncB64 = body["keyEncB64"];
    if (typeof ticket !== "string" || (keyEncB64 !== undefined && typeof keyEncB64 !== "string")) {
      throw unexpected();
    }
    return { kind: "password-change", grant: typeof keyEncB64 === "string" ? { ticket, keyEncB64 } : { ticket } };
  }
  if (!isIdEmail(body)) {
    throw unexpected();
  }
  adoptToken(body);
  return { kind: "session" };
}

export function completeWithCode(ticket: string, code: string): Promise<SecondFactorOutcome> {
  return completeSecondFactor(ticket, { code });
}

export function completeWithBackupCode(ticket: string, backupCode: string): Promise<SecondFactorOutcome> {
  return completeSecondFactor(ticket, { backupCode });
}

export function completeWithPasskey(ticket: string, credential: unknown): Promise<SecondFactorOutcome> {
  return completeSecondFactor(ticket, { credential });
}

/** Forgotten master password, step 1: proves the recovery key from the emergency kit. */
export async function startRecovery(email: string, recoveryAuthKeyB64: string): Promise<RecoveryStart> {
  const body = await requestJson("/api/v1/auth/recovery", { method: "POST", body: { email, recoveryAuthKeyB64 } });
  if (!isRecord(body) || typeof body["twoFactorRequired"] !== "boolean") {
    throw unexpected();
  }
  if (body["twoFactorRequired"]) {
    const ticket = body["ticket"];
    if (typeof ticket !== "string") {
      throw unexpected();
    }
    return { kind: "second-factor", ticket };
  }
  return { kind: "granted", grant: recoveryGrantOf(body) };
}

export interface RecoveryCompletion {
  readonly ticket: string;
  readonly saltB64: string;
  readonly authKeyB64: string;
  readonly keyEncB64: string;
  readonly recovery: { readonly keyEncB64: string; readonly authKeyB64: string };
}

/** Forgotten master password, step 2: new password and new kit in one go. Signs this device in. */
export async function completeRecovery(input: RecoveryCompletion): Promise<IdEmail> {
  const body = await requestJson("/api/v1/auth/recovery/complete", { method: "POST", body: { ...input, ...signInBody() } });
  if (!isIdEmail(body)) {
    throw unexpected();
  }
  adoptToken(body);
  return body;
}

export async function passkeyLoginOptions(ticket: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const body = await requestJson("/api/v1/webauthn/login/options", { method: "POST", body: { ticket } });
  if (!isRequestOptions(body)) {
    throw unexpected();
  }
  return body;
}

export interface PasskeySignIn extends IdEmail {
  /** The account keys sealed under the key from the passkey's PRF output. */
  readonly keyEncB64: string;
}

/** Sign-in with a passkey that has unlock turned on: the browser offers the passkeys it has for this site. */
export async function passkeyUnlockOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const body = await requestJson("/api/v1/webauthn/unlock/options", { method: "POST", body: {} });
  if (!isRequestOptions(body)) {
    throw unexpected();
  }
  return body;
}

/** Step-up with the master password: its auth key, checked against the stored verifier. */
export async function reauthWithPassword(authKeyB64: string): Promise<void> {
  await requestJson("/api/v1/auth/reauth", { method: "POST", body: { authKeyB64 } });
}

/** Step-up with a passkey, step 1: options for any passkey of the account, user verification required. */
export async function reauthOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  const body = await requestJson("/api/v1/auth/reauth/options", { method: "POST", body: {} });
  if (!isRequestOptions(body)) {
    throw unexpected();
  }
  return body;
}

export async function reauthWithPasskey(credential: unknown): Promise<void> {
  await requestJson("/api/v1/auth/reauth", { method: "POST", body: { credential } });
}

/** Must never carry the PRF output: the caller strips the client extension results. */
export async function passkeySignIn(credential: unknown): Promise<PasskeySignIn> {
  const body = await requestJson("/api/v1/webauthn/unlock/sign-in", { method: "POST", body: { credential, ...signInBody() } });
  if (!isIdEmail(body) || !isRecord(body) || typeof body["keyEncB64"] !== "string") {
    throw unexpected();
  }
  adoptToken(body);
  return { id: body.id, email: body.email, keyEncB64: body["keyEncB64"] };
}
